import React, { useEffect, useState } from 'react';
import { Grid, Paper, OutlinedInput, InputLabel, Button, Box, Typography, IconButton, Collapse, LinearProgress } from '@mui/material';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { dataGridLocaleConfig } from '../../../config/data-grid-locale';
import { useBudgetUnitSearchController } from './context/budget-unit-search-controller';
import { useBudgetUnitSearchState } from './context/budget-unit-search-context';
import BudgetUnitAppService from '../../../application/platform/sge/budget-unit/budget-unit-app-service';
import { IBudgetUnitResponse } from '../../../model/sge/budget-unit/response/budget-unit-response';

export function BudgetUnitSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [rows, setRows] = useState<IBudgetUnitResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const { handleOnChange, handleSearch, handleClearFilters, navigateToCreate, navigateToEdit } = useBudgetUnitSearchController();
    const { parameters, searchTrigger } = useBudgetUnitSearchState();

    useEffect(() => {
        setLoading(true);
        BudgetUnitAppService.list(parameters).then((r) => setRows(r)).finally(() => setLoading(false));
    }, [searchTrigger]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80, hide: true },
        { field: 'code', headerName: 'Código', width: 140 },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
        { field: 'year', headerName: 'Exercício', width: 110 },
        {
            field: 'actions', headerName: 'Ações', width: 80, sortable: false,
            renderCell: (params) => (
                <IconButton size="small" onClick={() => navigateToEdit(params.row.id)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Unidades Orçamentárias"
                    subtitle="Gerencie as unidades orçamentárias cadastradas"
                    breadcrumbs={[{ label: 'Unidades Orçamentárias' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, p: 1, ml: -1, mr: -1, mb: filtersOpen ? 2 : 0, '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6">Filtros</Typography>
                        </Box>
                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={filtersOpen}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={10}>
                                <InputLabel>Buscar por código ou nome</InputLabel>
                                <OutlinedInput fullWidth name="input-search" value={parameters.search || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InputLabel>Exercício</InputLabel>
                                <OutlinedInput fullWidth name="input-year" type="number" value={parameters.year || ''} onChange={handleOnChange} inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>Limpar</Button>
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Consultar</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ position: 'relative', pb: '28px' }}>
                    <Paper sx={{ p: 2, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            pageSize={100}
                            sx={{ flex: 1 }}
                            disableSelectionOnClick
                            experimentalFeatures={{ newEditingApi: true }}
                            components={{ LoadingOverlay: LinearProgress }}
                            localeText={dataGridLocaleConfig}
                        />
                    </Paper>
                    <SpeedDial ariaLabel="Nova Unidade Orçamentária" sx={{ position: 'absolute', bottom: 0, right: 16 }} onClick={navigateToCreate} icon={<SpeedDialIcon />} />
                </Box>
            </Grid>
        </React.Fragment>
    );
}
